import React from 'react';
import { useSelector } from 'react-redux';
import { GridPageLayout } from '@lux/grid';
import { Preset } from '@lux/text';
import { BrandColor } from '@lux/core';
import { StandardCarousel, Article } from '@browsing/standard-carousel';
import { getSemanticArticlesCarouselData } from '@client/redux/modules/semanticArticles/selectors';
import { ImageSize, useConfig } from '@browsing/config';
import { trackSelectContent, trackSelectItem, trackViewItemList } from '@browsing/tracking';
import { useTranslate } from '@pegasus/translation';
import { useVariant } from '@client/lib/assignments';
import { NavigationSourceType, CatalogType } from '@browsing/utils';
import { ElementType } from '@pegasus/tracker';
import { ErrorBoundary } from '@browsing/components';
import { CarouselContainer, AfterTitleLink } from './index.styles';
// @TODO - Keep it for future testing
// import { mockSemanticArticlesCarouselData } from './__mock__/sac';

const SemanticArticleCarouselInternal = () => {
    const config = useConfig();
    const {
        title: carouselTitle,
        articles: semanticArticles,
        totalCount,
        themeHash,
        loading,
        error,
    } = useSelector(getSemanticArticlesCarouselData);
    const translation = useTranslate();
    const isSacNewUIVariant = useVariant('sac-new-ui', 'variant_sac_new');

    if (!semanticArticles?.length) {
        return null;
    }

    const getLastSemanticArticleImage = () => {
        const lastArticle = semanticArticles[semanticArticles.length - 1];
        return config.getImage(lastArticle?.media![0].path, ImageSize.Medium);
    };

    const slicedSemanticArticles = semanticArticles.slice(0, 16);

    const sacCarouselValue = 'sac_carousel';

    const trackArticleSelection = (product: Article, idx: number) => {
        trackSelectItem(
            {
                element: sacCarouselValue,
                context: sacCarouselValue,
                item: product,
                itemListName: sacCarouselValue,
                themeHash,
            },
            {
                customMapper: () => ({ index: idx }),
            },
        );
    };

    const trackArticlesView = (items: Article[]) => {
        trackViewItemList(
            {
                items,
                context: sacCarouselValue,
                itemListName: sacCarouselValue,
                element: sacCarouselValue,
                themeHash,
            },
            {
                customMapper: () => ({
                    item_list_name: sacCarouselValue,
                }),
            },
        );
    };

    const trackArticlesScroll = () => {
        trackSelectContent(sacCarouselValue as unknown as ElementType, 'swipe', {
            themeHash,
        });
    };

    const trackSeeAllLinkClick = () => {
        trackSelectContent(sacCarouselValue as unknown as ElementType, 'click', {
            contentId: 'see_all',
            themeHash,
        });
    };

    const seeAllHref = `/campaigns/semantic-articles?themeHash=${themeHash}`;
    const seeAllText = translation('TP4U.cta', { count: totalCount });

    const afterTitle = isSacNewUIVariant ? (
        <AfterTitleLink
            color={BrandColor.primary}
            href={seeAllHref}
            preset={Preset.M}
            onClick={trackSeeAllLinkClick}
            link={true}
        >
            {seeAllText}
        </AfterTitleLink>
    ) : undefined;

    return (
        <GridPageLayout>
            <CarouselContainer>
                <StandardCarousel
                    title={carouselTitle}
                    carouselItems={slicedSemanticArticles as unknown as Article[]}
                    loading={loading}
                    error={!!error}
                    carouselId="semantic-articles-mylounge"
                    catalogType={CatalogType.SacCarousel}
                    themeHash={themeHash}
                    tracking={{
                        source: NavigationSourceType.SacCarousel,
                        trackArticleSelection,
                        trackArticlesView,
                        trackArticlesScroll,
                    }}
                    translationPath="article"
                    afterTitle={afterTitle}
                    seeAllConfig={{
                        image: getLastSemanticArticleImage(),
                        href: seeAllHref,
                        buttontext: seeAllText, // @TODO - change to component specific translation key
                        onClick: trackSeeAllLinkClick,
                    }}
                    navigateToPDPInCurrentTab={null}
                />
            </CarouselContainer>
        </GridPageLayout>
    );
};

export const SemanticArticleCarousel = () => (
    <ErrorBoundary componentName="SemanticArticleCarousel">
        <SemanticArticleCarouselInternal />
    </ErrorBoundary>
);
